import { useEffect, useState } from "react";

import { FORGOT_PASSWORD_PATH, LOGIN_PATH } from "@buildpy/core/modules/onboarding/rules";
import { Button } from "@buildpy/ui/button";
import Notice from "@buildpy/ui/notice";
import SectionCard from "@buildpy/ui/section-card";
import { BrandLogo } from "@buildpy/ui/brand-logo";
import { Link } from "@/lib/nav";
import { authCallbackOf, BOOT_HREF, getSupabase, type AuthCallback } from "@/lib/supabase";
import ResetPasswordForm from "./reset-form";

export type ResetScreen = "checking" | "ready" | "invalid";

export const RESET_INVALID_TITLE = "재설정 링크가 만료됐거나 잘못됐습니다";
export const RESET_INVALID_BODY = "링크는 한 번만, 정해진 시간 안에만 쓸 수 있습니다. 재설정 메일을 다시 요청하세요.";

// 복구 링크로 들어와 복구 세션이 섰을 때만 폼을 연다 — 그 밖(만료·오류·주소만 친 경우)은 안내.
export const resetScreenOf = (callback: AuthCallback, hasSession: boolean): ResetScreen =>
  callback.kind === "recovery" && hasSession ? "ready" : "invalid";

export const ResetPasswordView = ({ screen, onExpired }: { screen: ResetScreen; onExpired?: () => void }) => {
  if (screen === "invalid") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background p-4">
        <Notice
          actions={
            <>
              <Button asChild>
                <Link href={FORGOT_PASSWORD_PATH}>재설정 메일 다시 받기</Link>
              </Button>
              <Button asChild variant="outline">
                <Link href={LOGIN_PATH}>로그인 화면으로</Link>
              </Button>
            </>
          }
          body={RESET_INVALID_BODY}
          title={RESET_INVALID_TITLE}
        />
      </main>
    );
  }
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <SectionCard as="div" className="flex w-full max-w-sm flex-col gap-y-6" padding="lg">
        <div className="flex flex-col gap-y-3">
          <h1 className="flex">
            <BrandLogo size="lg" />
          </h1>
          <p className="text-sm text-muted-foreground">새 비밀번호를 두 번 입력하세요.</p>
        </div>
        {screen === "ready" ? (
          <ResetPasswordForm onExpired={onExpired ?? (() => undefined)} />
        ) : (
          <p className="text-sm text-muted-foreground">링크를 확인하는 중…</p>
        )}
      </SectionCard>
    </main>
  );
};

// 새 비밀번호 설정 — 재설정 메일(implicit #access_token&type=recovery)이 닿는 곳 · 셸을 읽지 않는다(복구 세션으로 /app 에 들어가지 않게).
const ResetPasswordPage = () => {
  const [screen, setScreen] = useState<ResetScreen>("checking");

  useEffect(() => {
    let alive = true;
    const callback = authCallbackOf(BOOT_HREF);
    void getSupabase()
      .auth.getSession()
      .then(
        ({ data }) => resetScreenOf(callback, Boolean(data.session)),
        () => "invalid" as const,
      )
      .then((next) => {
        if (!alive) return;
        // 토큰·오류가 붙은 주소를 기록에 남기지 않는다.
        window.history.replaceState(window.history.state, "", window.location.pathname);
        setScreen(next);
      });
    return () => {
      alive = false;
    };
  }, []);

  return <ResetPasswordView onExpired={() => setScreen("invalid")} screen={screen} />;
};

export default ResetPasswordPage;
